import React, { useEffect, useRef, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import useMediaQuery from '@mui/material/useMediaQuery';
import { JobViewStyled, RuleStyled, OverflowContainerStyled, ImageAltStyled } from '../../../styled/JobConnection';
import { useTheme } from '../Theme/ThemeContext';
import OvalSpinner from '../Spinners/OvalSpinner';
import { MdStopCircle } from 'react-icons/md';
import { FiMinimize2 } from 'react-icons/fi';
import { FaStream, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const ProcessingQueueView = ({ jobs = [], counts = { done: 0, total: jobs.length }, perJobStatuses = [], perJobStages = [], stopPending=false, onClose, onStop }) => {
  const { themeName } = useTheme();
  const isDark = themeName === 'dark';
  const isDesktop = useMediaQuery('(min-width:992px)');
  const headerRef = useRef(null);
  const [headerPad, setHeaderPad] = useState(12);

  useEffect(() => {
    const updatePad = () => {
      try {
        const h = headerRef.current ? headerRef.current.offsetHeight : 0;
        // Add a bit more breathing room on desktop so content never tucks under the sticky header
        const baseMin = isDesktop ? 16 : 8;
        const extra = isDesktop ? 12 : 4;
        const desktopExtraOffset = isDesktop ? 100 : 0;
        // On mobile, remove about 20px from the computed pad
        const mobileAdjustment = isDesktop ? 0 : -20;
        setHeaderPad(Math.max(baseMin, h + extra + desktopExtraOffset + mobileAdjustment));
      } catch(_) { /* noop */ }
    };
    updatePad();
    window.addEventListener('resize', updatePad);
    return () => window.removeEventListener('resize', updatePad);
  }, [isDesktop]);
  const total = (counts && counts.total != null ? counts.total : (jobs?.length || 0));
  const done = (counts && counts.done != null ? counts.done : 0);

  return (
    <JobViewStyled theme={themeName}>
        <span ref={headerRef} className="header processing-header" style={{ position: 'sticky', top: 0, zIndex: 1, background: 'inherit', paddingBottom: 4 }}>
            <h1 className="sectionTitle">Auto-Apply Progress</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            <button onClick={!stopPending ? onStop : undefined} title={stopPending ? 'Stopping…' : 'Stop all'} style={{ display: 'inline-flex', alignItems:'center', gap:6, cursor: stopPending ? 'wait' : 'pointer' }}>
                {stopPending ? (<OvalSpinner size={14} color="#EA4335" />) : (<MdStopCircle color="crimson" />)}
            </button>
            <button onClick={onClose} title="Minimize" style={{ display: 'inline-flex' }}>
                <FiMinimize2 color="#3E8F93" />
            </button>
            </div>
        </span>
        <OverflowContainerStyled style={{ flex: 1, minHeight: 0, paddingTop: headerPad }}>
            <RuleStyled />
            <div style={{ padding: '6px 0 8px 0', fontSize: '.8rem', color: isDark ? '#fff' : 'inherit' }}>
              Processing {done}/{total}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                {jobs.map((job, idx) => {
                const status = perJobStatuses[idx] || 'queued';
                // Default stage aligns with status: queued -> 'queued', running -> 'processing', success -> 'sent'
                const stage = perJobStages[idx] || (status === 'running' ? 'processing' : (status === 'success' ? 'sent' : 'processing'));
                const isSuccess = status === 'success';
                const isFailed = status === 'failed';
                                const stageLabel = (() => {
                                    switch(stage) {
                                        case 'email_discovery': return 'finding email';
                                        case 'email_normalize': return 'verifying email';
                                        case 'resume_optimize': return 'optimizing resume';
                                        case 'cover_letter': return 'generating cover letter';
                                        case 'sending_email': return 'sending';
                                        case 'sent': return 'sent';
                                        case 'processing': return 'processing';
                                        default: return stage || 'processing';
                                    }
                                })();
                return (
                <Card key={idx} sx={{ backgroundColor: 'transparent', borderRadius: '12px', border: '1px solid #c0d1d457' }}>
                    <CardContent sx={{ display: 'flex', alignItems: isDesktop ? 'center' : 'flex-start', gap: isDesktop ? 5 : 3, flexWrap: isDesktop ? 'nowrap' : 'wrap' }}>
                    <ImageAltStyled theme={themeName}>
                        {job?.company_object?.logo ? (
                            <img src={job.company_object?.logo} alt={job?.company_name || job?.company || 'Logo'} />
                        ) : (
                        <h1>{job?.company_name?.charAt(0) || job?.company?.charAt(0)}</h1>
                        )}
                    </ImageAltStyled>
                    <div style={{ flex: 1, minWidth: 0, color: isDark ? '#fff' : 'inherit', display: 'flex', flexDirection: 'column', gap: isDesktop ? 0 : 2 }}>
                        <div style={{ fontWeight: 600, lineHeight: 1.2 }}>{job?.title || job?.job_title}</div>
                        <div style={{ opacity: 0.8, fontSize: '.85rem' }}>{job?.company_name || job?.company}</div>
                                                {!isDesktop && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', color: isDark ? '#fff' : 'inherit', marginTop: 4 }}>
                                                        {isSuccess && (<><FaCheckCircle color="#41a956" /><span style={{ fontSize: '.8rem', color: isDark ? '#fff' : 'inherit' }}>sent</span></>)}
                                                        {isFailed && (<><FaTimesCircle color="crimson" /><span style={{ fontSize: '.8rem', color: isDark ? '#fff' : 'inherit' }}>failed</span></>)}
                                                        {!isSuccess && !isFailed && status === 'queued' && (
                                                            <><FaStream color="#5fbec5" /><span style={{ fontSize: '.8rem', color: isDark ? '#fff' : 'inherit' }}>in queue</span></>
                                                        )}
                                                        {!isSuccess && !isFailed && status !== 'queued' && (
                                                            <><OvalSpinner size={16} color="#3E8F93" /><span style={{ fontSize: '.8rem', color: isDark ? '#fff' : 'inherit' }}>{stageLabel || 'processing'}</span></>
                                                        )}
                                                </div>
                                                )}
                    </div>
                    {isDesktop && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', color: isDark ? '#fff' : 'inherit' }}>
                                                        {isSuccess && (<><FaCheckCircle color="#41a956" /><span style={{ fontSize: '.8rem', color: isDark ? '#fff' : 'inherit' }}>sent</span></>)}
                                                        {isFailed && (<><FaTimesCircle color="crimson" /><span style={{ fontSize: '.8rem', color: isDark ? '#fff' : 'inherit' }}>failed</span></>)}
                                                        {!isSuccess && !isFailed && status === 'queued' && (
                                                            <><FaStream color="#5fbec5" /><span style={{ fontSize: '.8rem', color: isDark ? '#fff' : 'inherit' }}>in queue</span></>
                                                        )}
                                                        {!isSuccess && !isFailed && status !== 'queued' && (
                                                            <><OvalSpinner size={16} color="#3E8F93" /><span style={{ fontSize: '.8rem', color: isDark ? '#fff' : 'inherit' }}>{stageLabel || 'processing'}</span></>
                                                        )}
                                                </div>
                    )}
                    </CardContent>
                </Card>
                );
            })}
            </div>
        </OverflowContainerStyled>
    </JobViewStyled>
  );
};

export default ProcessingQueueView;
